/**
 * NOS-162: slim campaign/match banner (research §C.6 — charity:water / TNC /
 * Habitat pattern). Message + optional deadline countdown + optional goal
 * progress + CTA. Deadline renders as static "N days left" text (SSR-safe,
 * no ticking JS). Dismissal is per-session and disabled in the builder.
 */
import { useState } from "react"
import type { BlockComponentProps } from "./shared"

/** Whole days from now until an ISO date; null when absent/invalid/past. */
export function daysUntil(deadline: string | undefined, now = Date.now()): number | null {
  if (!deadline) return null
  const ts = Date.parse(deadline)
  if (Number.isNaN(ts)) return null
  const days = Math.ceil((ts - now) / 86_400_000)
  return days >= 0 ? days : null
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString("en-US")}`
}

export function CampaignBannerBlock({ content, inBuilder, sectionId }: BlockComponentProps) {
  const message = (content?.message as string) ?? ""
  const deadline = content?.deadline as string | undefined
  const goal = content?.goal_amount as number | undefined
  const raised = content?.raised_amount as number | undefined
  const ctaText = (content?.cta_text as string) ?? ""
  const ctaUrl = (content?.cta_url as string) ?? "/donate"
  const dismissible = (content?.dismissible as boolean) ?? true

  const storageKey = `nos-banner-dismissed-${sectionId ?? "banner"}`
  const [dismissed, setDismissed] = useState(() => {
    if (inBuilder || typeof sessionStorage === "undefined") return false
    try {
      return sessionStorage.getItem(storageKey) === "1"
    } catch {
      return false
    }
  })

  if (dismissed || !message) return null

  const days = daysUntil(deadline)
  const pct =
    typeof goal === "number" && goal > 0 && typeof raised === "number"
      ? Math.min(100, Math.max(0, (raised / goal) * 100))
      : null

  const handleDismiss = () => {
    if (inBuilder) return
    setDismissed(true)
    try {
      sessionStorage.setItem(storageKey, "1")
    } catch {
      /* private mode — banner just reappears next load */
    }
  }

  return (
    <section
      role="region"
      aria-label="Campaign announcement"
      className="px-4 py-3 @sm:px-6 @md:px-8"
      style={{
        background: "var(--nos-color-primary, #3b82f6)",
        color: "var(--nos-color-text-on-primary, #ffffff)",
      }}
    >
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 text-center @md:flex-row @md:justify-between @md:text-left">
        <div className="flex flex-col gap-1 @sm:flex-row @sm:items-center @sm:gap-3">
          <p className="text-sm font-semibold @sm:text-base">{message}</p>
          {days !== null && (
            <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide">
              {days === 0 ? "Last day" : `${days} day${days === 1 ? "" : "s"} left`}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {pct !== null && (
            <div className="flex items-center gap-2" aria-label={`${formatMoney(raised!)} raised of ${formatMoney(goal!)} goal`}>
              <div className="h-2 w-24 overflow-hidden rounded-full bg-white/25 @sm:w-32">
                <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
              </div>
              <span className="whitespace-nowrap text-xs font-semibold">
                {formatMoney(raised!)} of {formatMoney(goal!)}
              </span>
            </div>
          )}
          {ctaText && (
            <a
              href={inBuilder ? undefined : ctaUrl}
              className="whitespace-nowrap rounded-lg bg-white px-4 py-1.5 text-sm font-bold transition-transform hover:scale-105"
              style={{ color: "var(--nos-color-primary, #3b82f6)" }}
            >
              {ctaText}
            </a>
          )}
          {dismissible && !inBuilder && (
            <button
              type="button"
              aria-label="Dismiss announcement"
              onClick={handleDismiss}
              className="text-white/70 transition-colors hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
